export type TabRoutedSearchBranch = {
  routeValues: string[]
  setFields: Array<{
    fieldName: string
    value: unknown
    valueKind: 'literal' | 'searchStringTrim' | 'searchStringNormalizeSpaces'
  }>
  reloadElementId?: string
  reloadElementName?: string
}

function normalizeScript(script: string): string {
  return script.replace(/\r\n/g, '\n')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function extractTabRouteFieldFromScript(script: string): string | null {
  const s = normalizeScript(script)
  if (!s) return null

  const paired = s.match(
    /en\.putData\s*\(\s*["']([^"']+)["']\s*\)\.getFieldAsString\s*\(\s*["']([^"']+)["']/,
  )
  if (paired?.[1] && paired[1] === paired[2]) return paired[1]

  const assign = s.match(
    /var\s+\w+\s*=\s*en\.(?:putData\s*\([^)]+\)\.)?getFieldAsString\s*\(\s*["']([^"']+)["']/,
  )
  if (assign?.[1]) return assign[1]

  return null
}

export function extractTabRouteVariableFromScript(script: string): string {
  const s = normalizeScript(script)
  const m = s.match(/var\s+(\w+)\s*=\s*en\.(?:putData\s*\([^)]+\)\.)?getFieldAsString\s*\(/)
  return m?.[1] ?? 'tab'
}

export function isTabRoutedSearchAction(script: string): boolean {
  if (!extractTabRouteFieldFromScript(script)) return false
  const tabVar = extractTabRouteVariableFromScript(script)
  return extractIfTabBlocks(script, tabVar).length > 0
}

function extractIfTabBlocks(script: string, tabVar: string): Array<{ condition: string; body: string }> {
  const blocks: Array<{ condition: string; body: string }> = []
  const re = new RegExp(`if\\s*\\(\\s*(${escapeRegExp(tabVar)}\\s*==[\\s\\S]*?)\\)\\s*\\{`, 'g')
  let match: RegExpExecArray | null
  while ((match = re.exec(script))) {
    const condition = match[1] ?? ''
    const start = match.index + match[0].length
    let depth = 1
    let i = start
    while (i < script.length && depth > 0) {
      const ch = script[i]
      if (ch === '{') depth += 1
      else if (ch === '}') depth -= 1
      i += 1
    }
    blocks.push({ condition, body: script.slice(start, i - 1) })
  }
  return blocks
}

function parseTabConditionValues(condition: string, tabVar: string): string[] {
  const values: string[] = []
  const re = new RegExp(`${escapeRegExp(tabVar)}\\s*==\\s*['"]([^'"]+)['"]`, 'g')
  let match: RegExpExecArray | null
  while ((match = re.exec(condition))) {
    if (match[1]) values.push(match[1])
  }
  return values
}

function parseSetFieldsFromBranchBody(
  body: string,
  routeField: string,
): TabRoutedSearchBranch['setFields'] {
  const out: TabRoutedSearchBranch['setFields'] = []

  if (/setField\s*\(\s*["']searchString["']/.test(body)) {
    if (body.includes('.replaceAll')) {
      out.push({ fieldName: 'searchString', value: null, valueKind: 'searchStringNormalizeSpaces' })
    } else if (body.includes('.trim()')) {
      out.push({ fieldName: 'searchString', value: null, valueKind: 'searchStringTrim' })
    }
  }

  const dynamicFieldRe =
    /setField\s*\(\s*en\.getElement\s*\(\s*(["']?[\w.]+["']?|\d+)\s*\)\.getName\s*\(\s*\)\s*\+\s*["']([^'"]+)["']\s*,\s*([^)]+)\)/g
  let dyn: RegExpExecArray | null
  while ((dyn = dynamicFieldRe.exec(body))) {
    const elementRef = String(dyn[1] ?? '').replace(/['"]/g, '')
    const suffix = dyn[2] ?? ''
    const rawValue = String(dyn[3] ?? '').trim()
    out.push({
      fieldName: `__element__:${elementRef}:${suffix}`,
      value: rawValue === 'true' ? true : rawValue === '1' ? 1 : rawValue,
      valueKind: 'literal',
    })
  }

  const literalRe = /setField\s*\(\s*["']([^'"]+)["']\s*,\s*([^;]+)\)/g
  let lit: RegExpExecArray | null
  while ((lit = literalRe.exec(body))) {
    const fieldName = lit[1] ?? ''
    if (!fieldName || fieldName === routeField) continue
    if (fieldName === 'searchString') continue
    const rawValue = String(lit[2] ?? '').trim()

    if (rawValue === 'true') out.push({ fieldName, value: true, valueKind: 'literal' })
    else if (rawValue === 'false') out.push({ fieldName, value: false, valueKind: 'literal' })
    else if (/^-?\d+$/.test(rawValue)) out.push({ fieldName, value: Number(rawValue), valueKind: 'literal' })
    else if (
      (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
      (rawValue.startsWith("'") && rawValue.endsWith("'"))
    ) {
      out.push({ fieldName, value: rawValue.slice(1, -1), valueKind: 'literal' })
    }
  }

  return out
}

function parseReloadFromBranchBody(
  body: string,
): Pick<TabRoutedSearchBranch, 'reloadElementId' | 'reloadElementName'> {
  const byId = body.match(/reloadElement\s*\(\s*(\d+)\s*\)/)
  if (byId?.[1]) return { reloadElementId: byId[1] }

  const byName = body.match(/reloadElement\s*\(\s*["']([^'"]+)["']\s*\)/)
  if (byName?.[1]) return { reloadElementName: byName[1] }

  return {}
}

export function parseTabRoutedSearchBranches(script: string): TabRoutedSearchBranch[] {
  if (!isTabRoutedSearchAction(script)) return []
  const normalized = normalizeScript(script)
  const routeField = extractTabRouteFieldFromScript(normalized) ?? ''
  const tabVar = extractTabRouteVariableFromScript(normalized)
  return extractIfTabBlocks(normalized, tabVar).map(({ condition, body }) => ({
    routeValues: parseTabConditionValues(condition, tabVar),
    setFields: parseSetFieldsFromBranchBody(body, routeField),
    ...parseReloadFromBranchBody(body),
  }))
}

export function findTabRoutedSearchBranch(script: string, routeValue: string): TabRoutedSearchBranch | null {
  const tab = String(routeValue ?? '').trim()
  if (!tab) return null
  for (const branch of parseTabRoutedSearchBranches(script)) {
    if (branch.routeValues.includes(tab)) return branch
  }
  return null
}

export function branchSetsTreeTableSearchFlag(branch: TabRoutedSearchBranch): boolean {
  return branch.setFields.some((field) => {
    if (field.fieldName.endsWith('__search')) return true
    if (field.fieldName.includes(':__search')) return true
    return false
  })
}

export function branchSetsTreeTableNextSearchFlag(branch: TabRoutedSearchBranch): boolean {
  return branch.setFields.some((field) => {
    if (field.fieldName.endsWith('__nextSearchPosition')) return true
    if (field.fieldName.includes(':__nextSearchPosition')) return true
    return false
  })
}

export function scriptHasDynamicTreeTableSearchFlag(script: string): boolean {
  return /getElement\s*\([^)]+\)\.getName\s*\(\s*\)\s*\+\s*["']__search["']/.test(script)
}

export function extractLiteralRouteFromPageScript(script: string, routeField: string): string {
  if (!routeField) return ''
  const re = new RegExp(
    `setField\\s*\\(\\s*["']${escapeRegExp(routeField)}["']\\s*,\\s*["']([^'"]+)["']\\s*\\)`,
  )
  return script.match(re)?.[1]?.trim() ?? ''
}

export function pageScriptSetsRouteViaTypesJoin(script: string, routeField: string): boolean {
  if (!routeField) return false
  const re = new RegExp(
    `setField\\s*\\(\\s*["']${escapeRegExp(routeField)}["']\\s*,\\s*types\\.join`,
  )
  return re.test(script)
}
